/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';

import ExerciseFilterGroup from './ExerciseFilterGroup';
import SCAnalysisTable from './SCAnalysisTable';
import ForcePlateDatePicker from '../../../force-plate-dashboard/components/presentational/ForcePlateDatePicker';
import ForcePlateDropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import { setSelectedAthleteGroup } from '../../ducks/sportCoachDashboardActions';
import { BoxShadow } from '../../../../shared/GlobalStyles';

const ColorNote = styled('div')`
  color: #444444;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: normal;
`;

const DashboardContainer = styled('div')`
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 15px;
  padding: 16px;
  position: relative;
`;

const DateOptionsContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  .downloadBtn {
    border: 1px solid #808080;
    border-radius: 50%;
    height: 35px;
    margin-left: 10px;
    padding: 3px;
    width: 35px;
  }
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const OptionsHeader = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ViewOnlyAnalysis = () => {
  const {
    athleteList,
    cardData,
    endDateCal,
    exerciseFiltersGroup,
    group,
    selectedAthleteGroup,
    startDateCal,
  } = useSelector((state) => state.sportCoachDashboard.data);

  const dispatch = useDispatch();

  const [columnHeaderHeight, setColumnHeaderHeight] = useState(null);

  const containerWidth = 1000;
  const hasData = cardData?.length > 0;

  // Logic to populate athlete dropdown
  const athleteDropdownList = useMemo(() => {
    if (!hasData || athleteList.length === 0) return [];
    return [{ fullName: 'View Group', userId: null }, ...athleteList];
  }, [hasData, athleteList]);

  // Manipulate and filter the data so it plays nicely with the GA table
  // Creating a new array with data attached to any athlete found in athleteList
  // and adding null data if exercise isn't found in return data
  // Data is filtered based on exerciseFiltersGroup and selectedAthleteGroup
  const manipulatedData = useMemo(() => {
    if (!hasData) return [];

    const shouldIncludeAthlete = (athlete) => !selectedAthleteGroup
      || selectedAthleteGroup.fullName === 'View Group'
      || (selectedAthleteGroup.fullName !== 'View Group'
        && selectedAthleteGroup.userId === athlete.userId);

    const buildAthleteResults = (athlete) => exerciseFiltersGroup.map((exerciseName) => {
      const exerciseData = cardData.find(
        (exercise) => exercise.metricName === exerciseName,
      );

      // If exercise data not found, push null data
      if (!exerciseData) {
        return {
          color: null,
          exerName: exerciseName,
          newestMax: null,
          oldestMax: null,
          percentChange: null,
        };
      }

      const athleteExerciseData = exerciseData.athleteData.find(
        (resultData) => resultData.userId === athlete.userId,
      );

      if (!athleteExerciseData) {
        return {
          color: null,
          exerName: exerciseName,
          newestMax: null,
          oldestMax: null,
          percentChange: null,
        };
      }

      return {
        color: athleteExerciseData.color,
        exerName: exerciseName,
        newestMax: athleteExerciseData.newestMax,
        oldestMax: athleteExerciseData.oldestMax,
        percentChange: athleteExerciseData.percentChange,
      };
    });

    return athleteList.filter(shouldIncludeAthlete).map((athlete) => ({
      fullName: athlete.fullName,
      pic: athlete.pic,
      results: buildAthleteResults(athlete),
      userId: athlete.userId,
    }));
  }, [
    hasData,
    athleteList,
    selectedAthleteGroup,
    exerciseFiltersGroup,
    cardData,
  ]);

  // Ensure consistency with group average data by manipulating and
  // filtering it similar to above
  const groupAverageData = useMemo(() => {
    if (!hasData) return [];

    return exerciseFiltersGroup.map((exerName) => {
      const exercise = cardData.find((exer) => exer.metricName === exerName);

      if (exercise) {
        return {
          color: exercise.color,
          metricName: exercise.metricName,
          newestAverage: exercise.newestAverage,
          oldestAverage: exercise.oldestAverage,
          percentChange: exercise.percentChange,
        };
      }

      // Add exercise with null values if not found in cardData
      return {
        color: null,
        metricName: exerName,
        newestAverage: null,
        oldestAverage: null,
        percentChange: null,
      };
    });
  }, [hasData, exerciseFiltersGroup, cardData]);

  const emptyFunction = () => undefined;

  const setAthlete = (option) => {
    if (option.fullName === 'View All') {
      dispatch(setSelectedAthleteGroup({ fullName: 'Viewing All' }, false));
    } else {
      dispatch(setSelectedAthleteGroup(option, true));
    }
  };

  const renderOptionsHeader = () => (
    <OptionsHeader>
      <MenuOptionsContainer>
        <ForcePlateDropDown
          defaultOption={group || {}}
          disabled
          headerWidthPx={175}
          label='name'
          list={['']}
          placeholder='Select a Group'
          readOnly
          selectItemFunction={emptyFunction}
        />
        {hasData && (
          <ForcePlateDropDown
            defaultOption={selectedAthleteGroup || {}}
            headerWidthPx={175}
            label='fullName'
            list={athleteDropdownList}
            placeholder='Select an Athlete'
            selectItemFunction={setAthlete}
          />
        )}
        <ExerciseFilterGroup />
      </MenuOptionsContainer>
      <DateOptionsContainer>
        <ForcePlateDatePicker
          disabled
          endDate={endDateCal.toDate()}
          setEndDate={emptyFunction}
          setStartDate={emptyFunction}
          startDate={startDateCal.toDate()}
        />
      </DateOptionsContainer>
    </OptionsHeader>
  );

  const renderTableContent = () => (manipulatedData.length > 0 ? (
    <SCAnalysisTable
      containerWidth={containerWidth}
      dataRows={manipulatedData}
      groupAverageData={groupAverageData}
      rowIsScrollable
      setColumnHeaderHeight={setColumnHeaderHeight}
    />
  ) : null);

  return (
    <DashboardContainer>
      {renderOptionsHeader()}
      <ColorNote>
        <span style={{ fontWeight: 'bold' }}>NOTE: </span>
        The column background color is determined by the amount of percent
        change. The percent change column will be red if the percent change is
        less than 3.00% or green if the percent greater than 0.00%.
      </ColorNote>
      {renderTableContent()}
    </DashboardContainer>
  );
};

export default ViewOnlyAnalysis;
